import { useState, useMemo } from 'react';
import { useUsers } from '../features/users';
import { Label } from '../components/common/Label';
import type { LabelVariant } from '../components/common/Label';
import { Tooltip } from '../components/common/Tooltip';
import { CreateButton } from '../components/common/CreateButton';
import { InviteUserModal } from '../components/users/InviteUserModal';
import { UserActionsMenu } from '../components/users/UserActionsMenu';
import type { User, RegisteredUser, ImpersonatorUser } from '../features/users/types';
import { useUser } from '../features/auth/useUser';
import { formatDateTime as formatDate } from '../utils/formatters';
import '../components/common/CommonTable.css';
import '../components/common/CommonFilters.css';
import './UserManagementPage.css';
import '../styles/buttons.css';
import '../styles/admin-pages.css';

type StatusFilter = 'all' | 'active' | 'inactive' | 'invited';
type RoleFilter = 'all' | 'admin' | 'user';

export default function UserManagementPage() {
  const usersQuery = useUsers();
  const { user: authUser } = useUser();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'admin':
        return '管理者';
      case 'user':
        return 'ユーザー';
      default:
        return '-';
    }
  };

  // ロールは OEM テーマに引きずられないよう固定色 (admin=ゴールド, user=グレー) で表示
  const getRoleClassName = (role?: string): string => {
    switch (role) {
      case 'admin':
        return 'role-badge role-admin';
      case 'user':
        return 'role-badge role-user';
      default:
        return 'role-badge role-unknown';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'アクティブ';
      case 'inactive':
        return '非アクティブ';
      case 'invited':
        return '招待中';
      case 'impersonator':
        return '代行アクセス';
      default:
        return status;
    }
  };

  const getStatusVariant = (status: string): LabelVariant => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'gray';
      case 'invited':
        return 'blue';
      case 'impersonator':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const isRegisteredUser = (user: User): user is RegisteredUser => {
    return user.type === 'registered';
  };

  const isImpersonatorUser = (user: User): user is ImpersonatorUser => {
    return user.type === 'impersonator';
  };

  // 論理削除ユーザーは email を deleted+{uid}@deleted.local に書き換えているので
  // email 表示時はそれをそのまま見せず「削除済み」と表記する
  const isDeletedEmail = (email: string | undefined | null): boolean => {
    if (!email) return false;
    return /^deleted\+.+@deleted\.local$/.test(email);
  };

  // ストレージ容量は常に MB 単位の整数で表示する (1MB 未満は 0 MB)
  const formatStorageBytes = (bytes: number | undefined | null): string => {
    if (!bytes || bytes <= 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  };

  const handleInviteSuccess = () => {
    usersQuery.refetch();
  };

  // ユーザー上限情報
  const maxUsers = usersQuery.data?.max_users ?? null;
  const activeCount = usersQuery.data?.active_count ?? 0;
  const invitedCount = usersQuery.data?.invited_count ?? 0;
  const canInvite = usersQuery.data?.can_invite ?? true;
  const currentUserCount = activeCount + invitedCount;
  const usagePercentage = maxUsers ? Math.round((currentUserCount / maxUsers) * 100) : 0;

  // フィルタ適用後のユーザー一覧
  const filteredUsers = useMemo(() => {
    const allUsers = usersQuery.data?.users ?? [];
    return allUsers.filter((user) => {
      if (statusFilter !== 'all' && user.status !== statusFilter) {
        return false;
      }
      if (roleFilter !== 'all') {
        // 招待中・代行アクセスユーザーはロールを持たないため、ロール絞り込み時は除外
        if (!isRegisteredUser(user)) return false;
        if (user.role !== roleFilter) return false;
      }
      return true;
    });
  }, [usersQuery.data?.users, statusFilter, roleFilter]);

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/multiple_neutral_2.svg" alt="" className="page-title-icon" />
            ユーザー管理
          </h1>
        </div>
        <div className="page-header-actions">
          {!canInvite ? (
            <Tooltip content="ユーザー数が上限に達しています。招待するにはプランをアップグレードするか、アクティブユーザー数の削減・招待のキャンセルを行ってください。" placement="bottom">
              <CreateButton disabled>ユーザーを招待</CreateButton>
            </Tooltip>
          ) : (
            <CreateButton onClick={() => setIsInviteModalOpen(true)}>ユーザーを招待</CreateButton>
          )}
        </div>
      </div>

      {/* ユーザー数カード */}
      {usersQuery.data && (
        <div className="admin-section-card" style={{ marginBottom: '24px', width: '320px', marginLeft: 'auto' }}>
          <div className="admin-section-card-header">
            <img src="/multiple_neutral_2.svg" alt="" className="admin-section-card-header-icon" />
            <span>ユーザー数</span>
          </div>
          <div className="admin-section-card-content" style={{ flexDirection: 'column', gap: '16px', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <span className="admin-stat-value" style={{ fontSize: '20px' }}>
                {currentUserCount}
                <span className="admin-stat-unit">人</span>
              </span>
              {maxUsers !== null && (
                <>
                  <span className="admin-stat-label" style={{ margin: '0 4px' }}>/</span>
                  <span className="admin-stat-label">
                    {maxUsers}
                    <span className="admin-stat-unit">人</span>
                  </span>
                </>
              )}
              {maxUsers === null && (
                <span className="admin-stat-label" style={{ marginLeft: '8px' }}>
                  (無制限)
                </span>
              )}
            </div>
            {maxUsers !== null && (
              <div style={{ width: '100%', padding: '0 16px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: 'rgba(var(--brand-primary-rgb) / 0.2)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.min(usagePercentage, 100)}%`,
                    height: '100%',
                    backgroundColor: usagePercentage >= 100 ? '#e57373' : usagePercentage >= 80 ? '#ffb74d' : 'var(--brand-primary)',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'rgba(245, 241, 230, 0.6)',
                }}>
                  <span>アクティブ: {activeCount}人 / 招待中: {invitedCount}人</span>
                  <span>{usagePercentage}% 使用中</span>
                </div>
              </div>
            )}
            {maxUsers === null && (
              <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', textAlign: 'center' }}>
                アクティブ: {activeCount}人 / 招待中: {invitedCount}人
              </div>
            )}
          </div>
        </div>
      )}

      {usersQuery.isLoading && (
        <div className="admin-loading">
          ユーザーを読み込み中...
        </div>
      )}

      {usersQuery.isError && (
        <div className="admin-error">
          ユーザーの読み込みに失敗しました
        </div>
      )}

      {usersQuery.data && usersQuery.data.users.length === 0 && (
        <div className="admin-empty-state">
          <div className="admin-empty-state-title">ユーザーが登録されていません</div>
          {!canInvite ? (
            <Tooltip content="ユーザー数が上限に達しています。招待するにはプランをアップグレードするか、アクティブユーザー数の削減・招待のキャンセルを行ってください。" placement="bottom">
              <button
                type="button"
                className="btn-modal"
                disabled
              >
                最初のユーザーを招待
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              className="btn-modal"
              onClick={() => setIsInviteModalOpen(true)}
            >
              最初のユーザーを招待
            </button>
          )}
        </div>
      )}

      {usersQuery.data && usersQuery.data.users.length > 0 && (
        <>
          <div className="list-controls">
            <div className="controls-row">
              <div className="filter-section">
                <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
                <span className="filter-label">フィルタ</span>
                <div className="filter-group">
                  <label htmlFor="status-filter">ステータス</label>
                  <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                    className="form-select"
                  >
                    <option value="all">すべて</option>
                    <option value="active">アクティブ</option>
                    <option value="inactive">非アクティブ</option>
                    <option value="invited">招待中</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="role-filter">権限</label>
                  <select
                    id="role-filter"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                    className="form-select"
                  >
                    <option value="all">すべて</option>
                    <option value="admin">管理者</option>
                    <option value="user">ユーザー</option>
                  </select>
                </div>
                <div className="total-count">
                  {filteredUsers.length === usersQuery.data.total ? (
                    <>全 <strong>{usersQuery.data.total}</strong> 件</>
                  ) : (
                    <><strong>{filteredUsers.length}</strong> / {usersQuery.data.total} 件</>
                  )}
                </div>
              </div>
            </div>
          </div>
          {filteredUsers.length === 0 ? (
            <div className="admin-empty-state">
              <div className="admin-empty-state-title">条件に合致するユーザーが見つかりませんでした</div>
            </div>
          ) : (
          <div className="table-container">
            <table className="users-table common-table">
            <thead>
              <tr>
                <th className="col-email">メールアドレス</th>
                <th className="col-display-name">表示名</th>
                <th className="col-role">ロール</th>
                <th className="col-status">ステータス</th>
                <th className="col-storage is-num">共有フォルダ</th>
                <th className="col-storage is-num">個人フォルダ</th>
                <th className="col-created-at">作成日時</th>
                <th className="col-actions">アクション</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const rowKey = isRegisteredUser(user)
                  ? user.id
                  : isImpersonatorUser(user)
                    ? `imp-${user.user_uid}`
                    : user.invitation_id;
                const isImpersonator = isImpersonatorUser(user);
                const sharedBytes = isRegisteredUser(user) || isImpersonatorUser(user)
                  ? user.shared_storage_bytes
                  : undefined;
                const personalBytes = isRegisteredUser(user) || isImpersonatorUser(user)
                  ? user.personal_storage_bytes
                  : undefined;
                const homeOrgLabel = isImpersonator
                  ? user.home_organization?.display_name || user.home_organization?.name || null
                  : null;
                return (
                  <tr key={rowKey} className={isImpersonator ? 'is-impersonator-row' : undefined}>
                    <td className="col-email">
                      <div className="user-email-cell">
                        {(isRegisteredUser(user) || isImpersonator) && user.picture_url && (
                          <img
                            src={user.picture_url}
                            alt=""
                            className="user-avatar"
                          />
                        )}
                        {isDeletedEmail(user.email) ? (
                          <span className="user-email user-email-deleted">削除済み</span>
                        ) : (
                          <span className="user-email">{user.email}</span>
                        )}
                      </div>
                    </td>
                    <td className="col-display-name">
                      {(() => {
                        const isDeleted = isRegisteredUser(user) && isDeletedEmail(user.email);
                        const labelText = (isRegisteredUser(user) || isImpersonator)
                          ? user.display_name || '-'
                          : '-';
                        return (
                          <span className={isDeleted ? 'user-display-name-deleted' : undefined}>
                            {labelText}
                          </span>
                        );
                      })()}
                      {isImpersonator && homeOrgLabel && (
                        <div className="user-impersonator-origin">{homeOrgLabel} から代行</div>
                      )}
                    </td>
                    <td className="col-role">
                      {isRegisteredUser(user) ? (
                        <span className={getRoleClassName(user.role)}>
                          {getRoleLabel(user.role)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="col-status">
                      <Label variant={getStatusVariant(user.status)}>
                        {getStatusLabel(user.status)}
                      </Label>
                    </td>
                    <td className="col-storage is-num">
                      {formatStorageBytes(sharedBytes)}
                    </td>
                    <td className="col-storage is-num">
                      {formatStorageBytes(personalBytes)}
                    </td>
                    <td className="col-created-at">
                      {isImpersonator ? '—' : formatDate(user.created_at)}
                    </td>
                    <td className="col-actions">
                      {isImpersonator ? (
                        '—'
                      ) : (
                        <UserActionsMenu
                          user={user}
                          currentUserUid={authUser?.user_uid}
                          onActionComplete={() => usersQuery.refetch()}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          )}
        </>
      )}

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvited={handleInviteSuccess}
      />
    </div>
  );
}
